'use client'

import { FormattedMessage } from 'react-intl'
import { find } from 'lodash'

import { Button, Icon, Text } from '@vinted/web-ui'
import { InfoCircle16 } from '@vinted/monochrome-icons'

import {
  cannotBundleButtonClickEvent,
  ReasonForNotBundling,
} from '@marketplace-web/bundles/items-bundle-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'

import { getIsBundlingAllowed } from '../../pages/BundleItems/utils'

type BundleItemFooterProps = {
  item: ProductItemModel
  items: Array<ProductItemModel>
  onClick: (item: ProductItemModel, index: number) => () => void
  sellerId: number
  itemIndex: number
  selectedItems: Array<ProductItemModel>
  isSelectedItemLimitReached: () => boolean
  bundleTrackingId: string | null
  onCantBundleButtonClick: () => void
  isProfilePage?: boolean
  screen: string
  disabled: boolean
}

const BundleItemFooter = ({
  item,
  items,
  onClick,
  sellerId,
  itemIndex,
  selectedItems,
  isSelectedItemLimitReached,
  bundleTrackingId,
  onCantBundleButtonClick,
  isProfilePage = false,
  screen,
  disabled = false,
}: BundleItemFooterProps) => {
  const { track } = useTracking()
  const translate = useTranslate('items_multiselect')

  const itemSelected = !!find(selectedItems, { id: item.id })
  const addTextSuffix = isProfilePage ? 'add_to_bundle' : 'add'
  const buttonTextSuffix = itemSelected ? 'remove' : addTextSuffix
  const testIdSuffix = itemSelected ? 'remove' : 'add'
  const isButtonDisabled = (isSelectedItemLimitReached() && !itemSelected) || disabled
  const trackingData = {
    listingId: screen === 'user_profile' ? item.id.toString() : item.id,
    sellerId: screen === 'user_profile' ? sellerId.toString() : sellerId,
    reasonForNotBundling: ReasonForNotBundling.ShippingSize,
    screen,
    bundleSessionId: bundleTrackingId,
  }

  const handleCantBundleButtonClick = () => {
    onCantBundleButtonClick()
    track(cannotBundleButtonClickEvent(trackingData))
  }

  if (
    getIsBundlingAllowed({ item, bundleItems: items, firstSelectedItemId: selectedItems[0]?.id })
  ) {
    return (
      <Button
        text={<FormattedMessage id={`items_multiselect.actions.${buttonTextSuffix}`} />}
        theme={itemSelected ? 'warning' : 'primary'}
        size="small"
        onClick={onClick(item, itemIndex)}
        disabled={isButtonDisabled}
        testId={`${testIdSuffix}-button`}
        aria={{
          'aria-label': itemSelected ? translate('a11y.remove') : undefined,
        }}
      />
    )
  }

  return (
    <Button
      text={<Text text={translate('actions.disabled')} as="span" type="caption" />}
      styling="flat"
      size="small"
      theme="muted"
      icon={<Icon name={InfoCircle16} />}
      iconPosition="right"
      testId="cant-bundle-button"
      onClick={handleCantBundleButtonClick}
    />
  )
}

export default BundleItemFooter
